import Page from 'src/components/Page';
import React, { useEffect, useState } from "react";
import {
    Container,
    createTheme,
    ThemeProvider,
    Skeleton,
    Button,
    IconButton,
    Stack,
    Divider,
    Box,
    Typography,
    Drawer,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Autocomplete,
    TextField,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import MUIDataTable, { ExpandButton } from "mui-datatables";
import { debounce } from 'src/utils/debounceSearch';
import axios from 'src/utils/apiAuth';
import { showToast, showErrorToast } from 'src/utils/Toast/SnackbarProvider';
import { CheckCircle, Cancel, Add, Delete, Close } from '@mui/icons-material';

const theme = createTheme({
    components: {
        MUIDataTable: {
            styleOverrides: {
                root: {
                    backgroundColor: '#red',
                },
                paper: {
                    boxShadow: 'none',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    "&.tss-1fz4yw6-MUIDataTableSelectCell-fixedLeft": {
                        position: "static !important",
                    },
                },
            },
        },
        MUIDataTableHeadCell: {
            styleOverrides: {
                root: {
                    fontWeight: '700',
                },
                data: {
                    fontWeight: '700',
                }
            }
        },
        MUIDataTableSelectCell: {
            styleOverrides: {
                headerCell: {
                    fontWeight: '500px'
                },
            },
        },
    },
    typography: {
        fontFamily: "Lexend"
    }
});

const components = {
    ExpandButton: function (props) {
        return <ExpandButton {...props} />;
    }
};

const PartnerSchema = Yup.object({
    first_name: Yup.string().max(50).required('First name is required'),
    middle_name: Yup.string().max(50).nullable(),
    last_name: Yup.string().max(50).required('Last name is required'),
    email: Yup.string().email('Enter a valid email').required('Email is required'),
    mobile: Yup.string().nullable(),
});

export default function Partners() {
    const [isLoading, setIsLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(0);
    const [count, setCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState();

    const [partnersData, setPartnersData] = useState([]);

    // Picker option sources + drawer/dialog state
    const [users, setUsers] = useState([]);
    const [countries, setCountries] = useState([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, partner: null });

    const debouncedSearch = debounce((query) => {
        setSearchQuery(query);
    }, 1000);

    // Fetch partners (API returns active-only by default — do not re-filter client-side)
    const fetchPartners = async (page, pageSize = 10, query = '', sortOrder = null) => {
        let apiUrl = `partner/?`;
        if (page + 1 && pageSize) apiUrl += `page=${page + 1}&page_size=${pageSize}&`;
        if (query) apiUrl += `search=${query}&`;
        if (sortOrder && sortOrder?.name && sortOrder?.direction) apiUrl += `sortfield=${sortOrder.name}&sortdirection=${sortOrder.direction}&`;

        // remove the trailing '&' from the URL
        if (apiUrl.endsWith("&")) apiUrl = apiUrl.slice(0, -1);

        axios.get(apiUrl)
            .then((response) => {
                if (response?.data) {
                    setPartnersData(response?.data?.results?.data || []);
                    setCount(response?.data?.count || 0);
                }
            })
            .catch(error => {
                if (error) {
                    showErrorToast(error);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // Picker option loaders (no role filter on users — D-3)
    const fetchUsers = async () => {
        try {
            const res = await axios.get('users-viewset?short_details=true');
            setUsers(res.data?.data || []);
        } catch (error) {
            showErrorToast('Error fetching users');
        }
    };

    const fetchCountries = async () => {
        try {
            const res = await axios.get('location?short_details=true');
            setCountries(res.data?.data || []);
        } catch (error) {
            showErrorToast('Error fetching countries');
        }
    };

    const formik = useFormik({
        initialValues: {
            first_name: '',
            middle_name: '',
            last_name: '',
            email: '',
            mobile: '',
            country: null,
            ba: null,
            bm: null,
            is_active: true,
        },
        validationSchema: PartnerSchema,
        onSubmit: async (values, { resetForm }) => {
            setIsSaving(true);
            try {
                // country/ba/bm are already pk-or-null; mobile is string-or-empty
                const payload = { ...values };
                let res;
                if (editingPartner) {
                    res = await axios.put(`partner/${editingPartner.id}/`, payload);
                } else {
                    res = await axios.post('partner/', payload);
                }
                showToast(res.data?.message);
                resetForm();
                setEditingPartner(null);
                setDrawerOpen(false);
                fetchPartners(currentPage, rowsPerPage, searchQuery, sortOrder);
            } catch (error) {
                const d = error.response?.data;
                if (d?.email) showErrorToast(d.email[0]);
                else if (d?.mobile) showErrorToast(d.mobile[0]);
                else showErrorToast(d?.message || 'Error saving partner');
            } finally {
                setIsSaving(false);
            }
        },
    });

    const { errors, touched, values, handleSubmit, getFieldProps, setFieldValue } = formik;

    const handleAdd = () => {
        setEditingPartner(null);
        formik.resetForm();
        setDrawerOpen(true);
    };

    // Edit pre-fill uses raw pks (p.country/p.ba/p.bm) so the pickers match (FR-013)
    const handleEdit = (id) => {
        axios.get(`partner/${id}/`)
            .then((res) => {
                const p = res.data.data;
                setEditingPartner(p);
                formik.setValues({
                    first_name: p.first_name,
                    middle_name: p.middle_name || '',
                    last_name: p.last_name,
                    email: p.email,
                    mobile: p.mobile || '',
                    country: p.country,
                    ba: p.ba,
                    bm: p.bm,
                    is_active: p.is_active,
                });
                setDrawerOpen(true);
            })
            .catch(() => {
                showErrorToast('Error loading partner');
            });
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setEditingPartner(null);
        formik.resetForm();
    };

    // Deactivate = soft delete via DELETE partner/{id}/ (record stays, drops from active list)
    const handleDeleteConfirm = () => {
        if (!deleteDialog.partner) return;
        axios.delete(`partner/${deleteDialog.partner.id}/`)
            .then((res) => {
                showToast(res.data?.message);
                setDeleteDialog({ open: false, partner: null });
                fetchPartners(currentPage, rowsPerPage, searchQuery, sortOrder);
            })
            .catch(() => {
                showErrorToast('Error deactivating partner');
            });
    };

    // MUI Data Table Options
    const options = {
        serverSide: true,
        selectableRows: "none",
        selectToolbarPlacement: 'none',
        filter: false,
        search: true,
        searchOpen: false,
        print: false,
        download: false,
        viewColumns: true,
        responsive: "standard",
        count: count,
        page: currentPage,
        rowsPerPage: rowsPerPage,
        rowsPerPageOptions: [10, 20, 30, 50, 100],
        onTableChange: (action, tableState) => {
            switch (action) {
                case 'changePage':
                    setCurrentPage(tableState.page);
                    break;
                case 'changeRowsPerPage':
                    setRowsPerPage(tableState.rowsPerPage);
                    break;
                case 'search':
                    debouncedSearch(tableState.searchText);
                    break;
                case 'sort':
                    setSortOrder(tableState.sortOrder);
                    break;
                default:
            }
        },
    };

    // MUI Data Table Columns — id is column 0 (hidden), read via tableMeta.rowData[0]
    const columns = [
        {
            name: "id",
            label: "ID",
            options: {
                display: false
            },
        },
        {
            name: "name",
            label: "Name",
            options: {
                customBodyRender: (value, tableMeta) => {
                    const id = tableMeta.rowData[0];
                    return (
                        <Button
                            variant="text"
                            onClick={() => handleEdit(id)}
                            sx={{
                                textTransform: 'none',
                                color: '#00AB55',
                                fontWeight: 'bold',
                                p: 0,
                                minWidth: 0,
                                justifyContent: 'flex-start',
                            }}
                        >
                            {value}
                        </Button>
                    );
                }
            }
        },
        {
            name: "email",
            label: "Email"
        },
        {
            name: "mobile",
            label: "Mobile"
        },
        {
            name: "country",
            label: "Country"
        },
        {
            name: "ba",
            label: "BA"
        },
        {
            name: "bm",
            label: "BM"
        },
        {
            name: "is_active",
            label: "Active",
            options: {
                customBodyRender: (value, tableMeta, updateValue) => {
                    if (value)
                        return <CheckCircle fontSize='medium' color='success' />
                    else
                        return <Cancel fontSize='medium' color='error' />
                }
            }
        },
        {
            name: "actions",
            label: "Actions",
            options: {
                sort: false,
                empty: true,
                customBodyRender: (value, tableMeta) => {
                    const id = tableMeta.rowData[0];
                    return (
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteDialog({ open: true, partner: { id } })}
                            title="Deactivate partner"
                        >
                            <Delete />
                        </IconButton>
                    );
                }
            }
        }
    ];

    useEffect(() => {
        fetchPartners(currentPage, rowsPerPage, searchQuery, sortOrder);
    }, [currentPage, rowsPerPage, searchQuery, sortOrder]);

    useEffect(() => {
        fetchUsers();
        fetchCountries();
    }, []);

    return (
        <Page title="Partners | CRM">
            <Container maxWidth="xl">
                {isLoading ? (
                    <div style={{ padding: '16px', marginTop: "20px" }}>
                        {/* Skeleton for the Table Header */}
                        <div style={{ display: 'flex', marginBottom: '20px' }}>
                            {columns.map((column, colIndex) => (
                                <Skeleton
                                    key={colIndex}
                                    variant="text"
                                    width={'30%'}
                                    style={{ marginRight: '16px' }}
                                    height={50}
                                />
                            ))}
                        </div>
                        {/* Skeletons for the Table Rows */}
                        {Array.from(new Array(rowsPerPage)).map((_, rowIndex) => (
                            <div key={rowIndex} style={{ display: 'flex', marginBottom: '10px' }}>
                                {columns.map((column, colIndex) => (
                                    <Skeleton
                                        key={colIndex}
                                        variant="text"
                                        width={'30%'}
                                        style={{ marginRight: '16px' }}
                                        height={50}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <ThemeProvider theme={theme}>
                        <MUIDataTable
                            title={
                                <Button
                                    variant="contained"
                                    startIcon={<Add />}
                                    onClick={handleAdd}
                                    sx={{
                                        backgroundColor: '#00AB55',
                                        fontWeight: 600,
                                        '&:hover': { backgroundColor: '#007B55' }
                                    }}
                                >
                                    Add Partner
                                </Button>
                            }
                            data={partnersData.map((p) => ({
                                id: p.id,
                                name: `${p.first_name} ${p.last_name}`,
                                email: p.email || '-',
                                mobile: p.mobile || '-',
                                country: p.country_details?.label || '-',
                                ba: p.ba_details ? `${p.ba_details.first_name} ${p.ba_details.last_name}` : '-',
                                bm: p.bm_details ? `${p.bm_details.first_name} ${p.bm_details.last_name}` : '-',
                                is_active: p.is_active,
                            }))}
                            columns={columns}
                            options={options}
                            components={components}
                        />
                    </ThemeProvider>
                )}

                {/* Drawer for Create/Edit Partner */}
                <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={handleCloseDrawer}
                    PaperProps={{
                        sx: { width: '25%', minWidth: 400 }
                    }}
                >
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                {editingPartner ? 'Edit Partner' : 'Add Partner'}
                            </Typography>
                            <IconButton onClick={handleCloseDrawer} size="small">
                                <Close />
                            </IconButton>
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        <FormikProvider value={formik}>
                            <Form onSubmit={handleSubmit}>
                                <Stack spacing={3}>
                                    <TextField
                                        label="First Name"
                                        {...getFieldProps('first_name')}
                                        error={Boolean(touched.first_name && errors.first_name)}
                                        helperText={touched.first_name && errors.first_name}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Middle Name"
                                        {...getFieldProps('middle_name')}
                                        error={Boolean(touched.middle_name && errors.middle_name)}
                                        helperText={touched.middle_name && errors.middle_name}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Last Name"
                                        {...getFieldProps('last_name')}
                                        error={Boolean(touched.last_name && errors.last_name)}
                                        helperText={touched.last_name && errors.last_name}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Email"
                                        {...getFieldProps('email')}
                                        error={Boolean(touched.email && errors.email)}
                                        helperText={touched.email && errors.email}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Mobile"
                                        {...getFieldProps('mobile')}
                                        error={Boolean(touched.mobile && errors.mobile)}
                                        helperText={touched.mobile && errors.mobile}
                                        fullWidth
                                    />

                                    {/* Country picker (keyed on .id, label option.label) */}
                                    <Autocomplete
                                        fullWidth
                                        options={countries}
                                        getOptionLabel={(o) => o.label || ''}
                                        value={countries.find((c) => c.id === values.country) || null}
                                        onChange={(e, v) => setFieldValue('country', v ? v.id : null)}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Country" />
                                        )}
                                    />

                                    {/* BA picker (keyed on .pk, no role filter — D-3) */}
                                    <Autocomplete
                                        fullWidth
                                        options={users}
                                        getOptionLabel={(o) => `${o.first_name} ${o.last_name}`}
                                        value={users.find((u) => u.pk === values.ba) || null}
                                        onChange={(e, v) => setFieldValue('ba', v ? v.pk : null)}
                                        renderInput={(params) => (
                                            <TextField {...params} label="BA" />
                                        )}
                                    />

                                    {/* BM picker (keyed on .pk, no role filter — D-3) */}
                                    <Autocomplete
                                        fullWidth
                                        options={users}
                                        getOptionLabel={(o) => `${o.first_name} ${o.last_name}`}
                                        value={users.find((u) => u.pk === values.bm) || null}
                                        onChange={(e, v) => setFieldValue('bm', v ? v.pk : null)}
                                        renderInput={(params) => (
                                            <TextField {...params} label="BM" />
                                        )}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values.is_active}
                                                onChange={(e) => setFieldValue('is_active', e.target.checked)}
                                            />
                                        }
                                        label="Active"
                                    />

                                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={handleCloseDrawer}
                                            sx={{ minWidth: 100 }}
                                        >
                                            Cancel
                                        </Button>
                                        <LoadingButton
                                            type="submit"
                                            variant="contained"
                                            loading={isSaving}
                                            startIcon={<Add />}
                                            sx={{
                                                minWidth: 120,
                                                backgroundColor: '#00AB55',
                                                '&:hover': { backgroundColor: '#007B55' }
                                            }}
                                        >
                                            {editingPartner ? 'Update' : 'Create'}
                                        </LoadingButton>
                                    </Stack>
                                </Stack>
                            </Form>
                        </FormikProvider>
                    </Box>
                </Drawer>

                {/* Deactivate confirmation dialog */}
                <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, partner: null })}>
                    <DialogTitle>Deactivate Partner</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to deactivate this partner? It will be hidden from the active list but not deleted.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialog({ open: false, partner: null })}>Cancel</Button>
                        <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                            Deactivate
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Page>
    );
}
